import { GetServerSidePropsContext } from 'next';
import useSWR from 'swr';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { productFetcher } from '@/data/Content/_Product';
import { getContractIdParamFromContext, useSettings } from '@/data/Settings';
import { ProductType, ResponseProductType } from '@/data/types/Product';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { expand, shrink } from '@/data/utils/keyUtil';
import { mapProductData } from '@/data/utils/mapProductData';
import { RequestParams } from 'integration/generated/query/http-client';
import { useMemo } from 'react';
import { CatSEO } from '../types/Category';
import { useUser } from '../User';

const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async (
		query: {
			storeId: string;
			catalogId: string;
			[key: string]: string | boolean | (string | number)[];
		},
		params: RequestParams
	): Promise<ProductType[]> => {
		const response = await productFetcher(pub, context)(query, params);
		const products = response?.contents ?? [];

		const productTypes = products.map((product: ResponseProductType) => mapProductData(product));

		return productTypes;
	};

const DATA_KEY = 'products';

type Args = {
	partNumber?: string[];
	id?: string[];
	contractId?: string | string[];
};

export const useProducts = ({ partNumber, id, contractId }: Args) => {
	const params = useExtraRequestParameters();
	const router = useNextRouter();
	const { settings } = useSettings();

	const { user } = useUser();
	const currentContract = getContractIdParamFromContext(user?.context);
	const contract = contractId ? { contractId } : currentContract;

	const {
		storeId,
		defaultCatalogId: catalogId,
		defaultCurrency: currency,
		langId,
	} = getClientSideCommon(settings, router);

	const {
		data = [],
		error,
		isLoading,
	} = useSWR(
		storeId && (partNumber?.length || id?.length)
			? [
					shrink({
						storeId,
						catalogId,
						langId,
						currency,
						partNumber,
						id,
						...contract,
					}),
					DATA_KEY,
			  ]
			: null,
		async ([props]) => fetcher(true)(expand(props), params)
	);

	return {
		products: data,
		loading: !error && isLoading,
		error,
	};
};

export const useProductsWithParent = (args: Args) => {
	const { products, loading: productsLoading, error: productsError } = useProducts(args);

	const parentIds = useMemo(() => {
		if (!products.length) {
			return [];
		}

		return products.map((product) => product.parentCatalogEntryID);
	}, [products]);

	const {
		products: parentProducts,
		loading: parentProductsLoading,
		error: parentProductsError,
	} = useProducts({
		id: parentIds,
	});

	const productsWithParent = useMemo(() => {
		if (!products.length || !parentProducts.length) {
			return [];
		}

		return products.map((product) => {
			const parentProduct = parentProducts.find(
				(parentProduct) => parentProduct.id === product.parentCatalogEntryID
			);

			return {
				...product,
				seo: parentProduct?.seo ?? ({} as CatSEO),
				parentProduct,
			};
		});
	}, [products, parentProducts]);

	return {
		products: productsWithParent,
		loading: productsLoading || parentProductsLoading,
		error: productsError || parentProductsError,
	};
};
